/**
 * What a line carries and how big it is, read from its line number
 * (`<size>"-<SERVICE>-<seq>-<class>`, e.g. `16"-150S-1074-3CS1S`). The Line map
 * colours a pipe by its contents and draws it as thick as its size.
 *
 * SERVICE_FLUID mirrors ARAMCO_SERVICE_FLUID in
 * packages/kg-generator-core/src/line-spec-decode.ts (SAES-L-105 §7.2.5), the
 * source of truth; a parity test keeps the two equal. It is copied rather than
 * imported because the dev server only serves linked packages from packages/.
 * A code that is not in the standard's table keeps its raw code and no meaning.
 */
export const SERVICE_FLUID: ReadonlyMap<string, string> = new Map([
  ['A', 'Air'], ['AH', 'Acid Hydrocarbon'], ['AS', 'Acid Sewer'], ['BBD', 'Boiler Blowdown'], ['BD', 'Blowdown'],
  ['BFW', 'Boiler Feed Water'], ['BS', 'Bio-Sludge'], ['C', 'Chemical'], ['CA', 'Caustic'], ['CAS', 'Caustic Sewer'],
  ['CAT', 'Catalyst'], ['CS', 'Chemical Sewer'], ['CW', 'Chilled Water'], ['CWR', 'Cooling Water Return'],
  ['CWS', 'Cooling Water Supply'], ['DGA', 'Diglycolamine'], ['DFW', 'Deaerator Feed Water'], ['DMW', 'Demineralized Water'],
  ['DSW', 'Distilled Water'], ['DT', 'Duct Trims'], ['DW', 'Drinking Water'], ['E', 'Exhaust Steam'],
  ['EIA', 'Emergency Instrument Air'], ['FG', 'Fuel Gas'], ['FGH', 'High Pressure Fuel Gas'], ['FGL', 'Low Pressure Fuel Gas'],
  ['FLO', 'Flushing Oil'], ['FO', 'Fuel Oil'], ['FW', 'Fire Water'], ['GG', 'Gart Gas'], ['H', 'Hydrogen'],
  ['HCL', 'Hydrochloric Acid'], ['HO', 'Hydraulic Oil'], ['HSG', 'Hydrogen Sulfide Gas'], ['IA', 'Instrument Air'],
  ['LO', 'Lube Oil'], ['ME', 'Methanol'], ['MO', 'Mist Oil'], ['N', 'Nitrogen'], ['NG', 'Natural Gas'], ['OS', 'Oily Sludge'],
  ['OW', 'Oily Water'], ['OWS', 'Oily Water Sewer'], ['P', 'Oil & Oil Products'], ['PA', 'Process Air'], ['PE', 'Pond Effluent'],
  ['PG', 'Purge Gas'], ['PO', 'Pump Out'], ['PT', 'Pump Trims'], ['PW', 'Process Water'], ['R', 'Refrigerants'],
  ['RL', 'Relief Line'], ['RLC', 'Cold Relief Line'], ['RLW', 'Warm Relief Line'], ['RW', 'Raw Water'], ['S', 'Steam'],
  ['SA', 'Sulfuric Acid'], ['SC', 'Steam Condensate'], ['SCA', 'Spent Caustic'], ['SF', 'Sulfur'], ['SO', 'Seal Oil'],
  ['SOW', 'Sour Water'], ['SPO', 'Slop Oil'], ['SR', 'Sewer (Storm)'], ['SW', 'Salt Water'], ['SWS', 'Sanitary Sewer'],
  ['TPW', 'Tempered Water'], ['TW', 'Treated Water'], ['UA', 'Utility Air'], ['UW', 'Utility Water'], ['VG', 'Vent Gas'],
  ['VT', 'Vessel Trim'], ['W', 'Water'], ['WW', 'Waste Water'],
  ['600C', '600 psig H.P. Condensate'], ['150C', '150 psig M.P. Condensate'], ['60C', '60 psig L.P. Condensate'], ['15C', '15 psig L.P. Condensate'],
  ['600S', '600 psig H.P. Steam'], ['150S', '150 psig M.P. Steam'], ['60S', '60 psig L.P. Steam'], ['15S', '15 psig L.P. Steam'],
])

export type Family = 'process' | 'steam' | 'condensate' | 'water' | 'refrigerant' | 'inert' | 'gas' | 'relief' | 'other' | 'unknown'

/** one colour per family of contents; no reds (red marks problems in these views) */
export const FAMILY: Record<Family, { label: string; color: string }> = {
  process: { label: 'Oil & process', color: '#0072CE' },
  steam: { label: 'Steam', color: '#F57C00' },
  condensate: { label: 'Condensate', color: '#26A69A' },
  water: { label: 'Water', color: '#00A6D6' },
  refrigerant: { label: 'Refrigerant', color: '#00A651' },
  inert: { label: 'Nitrogen & air', color: '#8E44AD' },
  gas: { label: 'Fuel & process gas', color: '#9E9D24' },
  relief: { label: 'Relief & blowdown', color: '#D81B9C' },
  other: { label: 'Chemicals, sewers & other', color: '#795548' },
  unknown: { label: 'Code not in the service table', color: '#455A64' },
}

function familyOf(fluid: string | undefined): Family {
  if (!fluid) return 'unknown'
  if (/Condensate/.test(fluid)) return 'condensate'
  if (/Steam/.test(fluid)) return 'steam'
  if (/Refrigerant/.test(fluid)) return 'refrigerant'
  if (/Relief|Blowdown/.test(fluid)) return 'relief'
  if (/Sewer|Sludge|Effluent/.test(fluid)) return 'other'
  if (/Nitrogen|Air/.test(fluid)) return 'inert'
  if (/Gas|Hydrogen/.test(fluid)) return 'gas'
  if (/Water/.test(fluid)) return 'water'
  if (/Oil|Products|Hydrocarbon/.test(fluid)) return 'process'
  return 'other'
}

/** steam and condensate are shaded by pressure: lighter is lower */
function shade(hex: string, code: string): string {
  const p = /^(\d+)[SC]$/.exec(code)?.[1]
  const f = p === '15' || p === '60' ? 0.35 : p === '600' ? -0.3 : 0
  if (!f) return hex
  const ch = (i: number) => {
    const v = parseInt(hex.slice(i, i + 2), 16)
    return Math.round(f > 0 ? v + (255 - v) * f : v * (1 + f)).toString(16).padStart(2, '0')
  }
  return `#${ch(1)}${ch(3)}${ch(5)}`
}

export interface LineAttrs { sizeIn: number | null; service: string | null; fluid?: string; family: Family; color: string }

const LINE_NO = /^\s*(\d+-\d+\/\d+|\d+\/\d+|\d+(?:\.\d+)?)\s*"\s*-\s*([A-Z0-9]+)\s*-/i

const sizeOf = (s: string): number => {
  const mixed = /^(\d+)-(\d+)\/(\d+)$/.exec(s), frac = /^(\d+)\/(\d+)$/.exec(s)
  return mixed ? +mixed[1] + +mixed[2] / +mixed[3] : frac ? +frac[1] / +frac[2] : +s
}

/** size (inches), service code, its fluid and the colour for it; nulls when the number has another shape */
export function lineAttrs(lineNo: string | undefined): LineAttrs {
  const m = LINE_NO.exec(lineNo ?? '')
  if (!m) return { sizeIn: null, service: null, family: 'unknown', color: FAMILY.unknown.color }
  const service = m[2].toUpperCase(), fluid = SERVICE_FLUID.get(service), family = familyOf(fluid)
  return { sizeIn: sizeOf(m[1]), service, fluid, family, color: shade(FAMILY[family].color, service) }
}

/** stroke width (px) for a pipe of this size: thicker for bigger pipes, readable at both ends */
export const sizeWidth = (sizeIn: number | null): number =>
  sizeIn == null ? 4 : Math.max(3, Math.min(13, 2.5 + 1.6 * Math.sqrt(sizeIn)))
